import { useEffect } from "react";
import dynamic from "next/dynamic";
import {
  Hits,
  Configure,
  Stats,
  Index,
  Pagination,
  useInstantSearch,
  useSearchBox,
} from "react-instantsearch";

import Sites from "src/constants/Sites";
import { getAlgoliaIndexName } from "helpers/Helper";
import { useAppContext } from "@components/Provider";
const SearchTabs = dynamic(() => import("@components/AlgoliaSearchPage/SearchTabs"));
const NoResults = dynamic(() => import("@components/AlgoliaSearchPage/NoResults"));
const PostResult = dynamic(() => import("@components/AlgoliaSearchPage/PostResult.js"));
const AuthorResult = dynamic(() => import("@components/AlgoliaSearchPage/AuthorResult"));
const BookResults = dynamic(() => import("@components/AlgoliaSearchPage/BookResults"));
const LoadingBoundary = dynamic(() => import("@components/AlgoliaSearchPage/LoadingBoundary"));
const NoResultsBoundary = dynamic(() => import("@components/AlgoliaSearchPage/NoResultsBoundary"));

import styles from "./Search.module.css";
import "./Search.css";

const AlgoliaSearchResults = (props) => {
  const {
    setSearchText,
    handleSearchIndex,
    searchText: stateSearchText,
    searchIndex: stateSearchIndex,
    urlSearchIndex,
  } = props;

  const { siteId } = useAppContext();
  const { results, uiState, setUiState, refresh } = useInstantSearch();

  //Execute Search based on search term and search index from query when page loads
  useEffect(() => {
    setSearchText(decodeURIComponent(stateSearchText));

    if (getAlgoliaIndexName(urlSearchIndex, siteId) !== stateSearchIndex) {
      handleSearchIndex(urlSearchIndex, siteId, "Search page useffect");
    }

    // Update the UI state of the Algolia search instance to include the current search query.
    // This ensures that the search UI components are synchronized with the URL parameters.
    setUiState({
      ...uiState,
      [stateSearchIndex]: {
        query: stateSearchText,
      },
    });
    refresh();

    return () => {
      setSearchText("");
    };
  }, [stateSearchText, stateSearchIndex]);

  const renderLoader = () => {
    return <div className={styles["search__loader"]}>Searching...Please Wait</div>;
  };

  const renderNoResults = () => {
    return <NoResults searchTerm={stateSearchText} searchIndex={urlSearchIndex} />;
  };

  const renderHits = () => {
    let hit;
    switch (urlSearchIndex) {
      case "articles":
        hit = (
          <Index indexName={siteId + "_POST"}>
            <Stats />
            <Configure hitsPerPage={20} />
            <LoadingBoundary fallback={renderLoader()}>
              <NoResultsBoundary fallback={renderNoResults()}>
                <Hits hitComponent={PostResult} />
                {renderPagination()}
              </NoResultsBoundary>
            </LoadingBoundary>
          </Index>
        );
        break;
      case "authors":
        hit = (
          <Index indexName="ORM_AUTHORS">
            <Stats key="author_stats" />
            <Configure hitsPerPage={20} />
            <LoadingBoundary fallback={renderLoader()}>
              <NoResultsBoundary fallback={renderNoResults()}>
                <Hits hitComponent={AuthorResult} key="author_hits" />
                {renderPagination()}
              </NoResultsBoundary>
            </LoadingBoundary>
          </Index>
        );
        break;
      case "genres":
        hit = (
          <Index indexName="ORM_BOOKS">
            <Stats />
            <Configure hitsPerPage={20} restrictSearchableAttributes={["customcategory"]} />
            <LoadingBoundary fallback={renderLoader()}>
              <NoResultsBoundary fallback={renderNoResults()}>
                <BookResults />
                {renderPagination()}
              </NoResultsBoundary>
            </LoadingBoundary>
          </Index>
        );
        break;
      default:
        hit = (
          <Index indexName="ORM_BOOKS">
            <Stats />
            <Configure hitsPerPage={20} />
            <LoadingBoundary fallback={renderLoader()}>
              <NoResultsBoundary fallback={renderNoResults()}>
                <BookResults />
                {renderPagination()}
              </NoResultsBoundary>
            </LoadingBoundary>
          </Index>
        );
        break;
    }
    return hit;
  };

  const renderPagination = () => {
    return results?.hits?.length >= 20 && <Pagination showLast padding={3} key="ais_pagination" />;
  };

  const renderSearchTabs = () => {
    const tabs = [];

    if (siteId === Sites.EARLYBIRDBOOKS || siteId === Sites.OPENROADMEDIA) {
      tabs.push(
        <div key="books" label="books">
          {renderHits()}
        </div>,
        <div key="authors" label="authors">
          {renderHits()}
        </div>,
        <div key="genres" label="genres">
          {renderHits()}
        </div>
      );
    } else {
      tabs.push(
        <div key="articles" label="articles">
          {renderHits()}
        </div>
      );
    }

    if (siteId === Sites.EARLYBIRDBOOKS) {
      tabs.splice(
        2,
        0,
        <div key="articles" label="articles">
          {renderHits()}
        </div>
      );
    }

    return tabs;
  };

  return (
    <>
      <div
        className={styles["search-container"]}
        style={{
          "--ais-Hits-list-flex-direction": urlSearchIndex === "articles" ? "row" : "column",
          "--ais-Hits-list-flex-wrap": urlSearchIndex === "articles" ? "wrap" : "nowrap",
          "--ais-Hits-item-max-width": urlSearchIndex === "articles" ? "24%" : "100%",
          "--ais-Hits-item-media-max-width": urlSearchIndex === "articles" ? "50%" : "100%",
        }}
      >
        <div className={styles["search-title"]}>
          Showing results for <span>{stateSearchText}</span>
        </div>
        <SearchTabs
          searchIndex={urlSearchIndex}
          search={stateSearchText}
          handleSearchIndex={handleSearchIndex}
        >
          {renderSearchTabs()}
        </SearchTabs>
      </div>
    </>
  );
};

// Defines a higher-order component (HOC) to connect a given component with Algolia's SearchBox.
// This HOC enriches the passed component with search-related props using Algolia's useSearchBox hook.
const connectSearchBox = (Component) => {
  // Defines the functional component that wraps the original component.
  const SearchBox = (props) => {
    // Uses Algolia's useSearchBox hook to get search-related data and functions.
    // This includes functions to manipulate the search state and data about the current search state.
    const data = useSearchBox();

    // Renders the original component, passing all original props along with the search data from useSearchBox.
    // This allows the original component to access and utilize search functionality provided by Algolia.
    return <Component {...props} {...data} />;
  };

  // Returns the enhanced component with search capabilities.
  return SearchBox;
};

export default connectSearchBox(AlgoliaSearchResults);
